// Libraries
import React from 'react';

// Supermove
import {DataLoader, Icon, Space, Styled} from '@supermove/components';
import {gql, useQuery} from '@supermove/graphql';
import {ResponsiveType, useMemo, useResponsive} from '@supermove/hooks';
import {AccountGrantModel, UserModel} from '@supermove/models';
import {colors} from '@supermove/styles';

// App
import Callout from '@shared/design/components/Callout';
import Table, {ColumnDefinitionType, TableComponents} from '@shared/design/components/Table';
import Text from '@shared/design/components/Text';
import TextTooltip from '@shared/design/components/TextTooltip';
import PageLoadingIndicator from '@shared/modules/App/components/PageLoadingIndicator';
import AccountGrantActionsPopover from 'modules/Organization/Settings/Communication/components/CommunicationAccounts/components/AccountActionsPopover';
import ExpiredPrimaryAccountBanner from 'modules/Organization/Settings/Communication/components/CommunicationAccounts/components/ExpiredPrimaryAccountBanner';
import {sortAccountGrants} from 'modules/Organization/Settings/Communication/components/CommunicationAccounts/components/sortAccountGrants';
import {EditAccessType} from 'modules/Organization/Settings/Communication/components/CommunicationAccounts/getEditAccess';
import {sortBranches} from 'modules/Organization/Settings/Communication/components/CommunicationAccounts/sortBranches';

const StatusContainer = Styled.View<{color: string}>`
  border-radius: 4px;
  background-color: ${({color}) => color};
  padding-vertical: 4px;
  padding-horizontal: 8px;
`;

const BranchesContainer = Styled.View`
  flex-direction: row;
  flex-wrap: wrap;
  flex: 1;
`;

const MobileTopRow = Styled.View`
  flex: 1;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const TooltipContainer = Styled.View`
`;

const StatusCell = ({accountGrant}: {accountGrant: AccountGrantModel}) => {
  const isActive = accountGrant.status === 'ACTIVE';
  const isPrimaryDisconnected = accountGrant.isPrimary && !isActive;
  return (
    <TextTooltip
      text={
        isPrimaryDisconnected
          ? 'All emails will be sent from the Supermove address until the primary account is reconnected.'
          : undefined
      }
    >
      <TooltipContainer>
        <StatusContainer color={isActive ? colors.green.accent : colors.red.accent}>
          <Text.MicroLabel style={{color: isActive ? colors.green.status : colors.red.warning}}>
            {isActive ? 'Connected' : 'Disconnected'}
          </Text.MicroLabel>
        </StatusContainer>
      </TooltipContainer>
    </TextTooltip>
  );
};

const getBranchTooltip = (accountGrant: AccountGrantModel, isPrimaryGrantDisconnected: boolean) => {
  if (isPrimaryGrantDisconnected) {
    return 'This branch will use the Supermove email address until the primary account is reconnected.';
  } else if (accountGrant.status !== 'ACTIVE') {
    return 'This branch will send emails with the primary account until this account is reconnected. ';
  }
};

const BranchCell = ({
  accountGrant,
  isPrimaryGrantDisconnected,
}: {
  accountGrant: AccountGrantModel;
  isPrimaryGrantDisconnected: boolean;
}) => {
  const branches = useMemo(() => {
    return accountGrant.accountGrantOrganizationAssignments
      .map((assignment) => assignment.organization)
      .filter((organization) => !organization.isDeleted)
      .sort(sortBranches);
  }, [accountGrant]);

  if (branches.length === 0) {
    return <Text.Body style={{fontStyle: 'italic', color: colors.gray.tertiary}}>None</Text.Body>;
  }

  return (
    <BranchesContainer>
      {branches.map((branch) => {
        return (
          <React.Fragment key={branch.id}>
            <TextTooltip
              text={getBranchTooltip(accountGrant, isPrimaryGrantDisconnected)}
              placement={'top'}
            >
              <TooltipContainer>
                <StatusContainer
                  color={colors.gray.border}
                  style={{marginVertical: 4, marginHorizontal: 4}}
                >
                  <Text.MicroLabel>{branch.name}</Text.MicroLabel>
                </StatusContainer>
                <Space width={4} />
              </TooltipContainer>
            </TextTooltip>
          </React.Fragment>
        );
      })}
    </BranchesContainer>
  );
};

const getColumnDefinitions = ({
  handleUpdateAccountGrant,
  responsive,
  editAccess,
  primaryAccountGrant,
}: {
  handleUpdateAccountGrant: (accountGrant: AccountGrantModel) => void;
  responsive: ResponsiveType;
  primaryAccountGrant?: AccountGrantModel;
  editAccess: EditAccessType;
}): ColumnDefinitionType<AccountGrantModel>[] => {
  return [
    {
      headerLabel: 'Status',
      flex: 1,
      cellComponent: (accountGrant) => {
        return <StatusCell accountGrant={accountGrant} />;
      },
      mobileOptions: {
        isInHeader: true,
        rank: 2,
      },
    },
    ...(responsive.desktop
      ? [
          {
            headerLabel: 'Email Address',
            flex: 2,
            cellComponent: (accountGrant: AccountGrantModel) => {
              return <Text.MicroLabel>{accountGrant.identifier}</Text.MicroLabel>;
            },
          },
        ]
      : [
          {
            headerLabel: 'Email Address',
            flex: 2,
            cellComponent: (accountGrant: AccountGrantModel) => {
              const hasEditAccess = editAccess === 'EDIT';
              return (
                <MobileTopRow>
                  <Text.MicroLabel>{accountGrant.identifier}</Text.MicroLabel>
                  {hasEditAccess ? (
                    <AccountGrantActionsPopover
                      accountGrant={accountGrant}
                      handleUpdateAccountGrant={handleUpdateAccountGrant}
                      currentPrimaryAccount={primaryAccountGrant?.identifier}
                    />
                  ) : null}
                </MobileTopRow>
              );
            },

            mobileOptions: {
              isInHeader: true,
              rank: 1,
            },
          },
        ]),
    {
      headerLabel: 'Provider',
      flex: 1,
      cellComponent: (accountGrant) => {
        return (
          <Text.MicroLabel style={{textTransform: 'capitalize'}}>
            {accountGrant.nylasGrantProvider}
          </Text.MicroLabel>
        );
      },
    },
    ...(responsive.desktop
      ? [
          {
            headerLabel: 'Primary',
            flex: 1,
            cellComponent: (accountGrant: AccountGrantModel) => {
              return accountGrant.isPrimary ? (
                <Icon source={Icon.Check} color={colors.gray.secondary} />
              ) : null;
            },
            emptyText: '',
          },
        ]
      : [
          {
            cellComponent: (accountGrant: AccountGrantModel) => {
              if (accountGrant.isPrimary) {
                return <Text.Body>{'Primary Account'}</Text.Body>;
              }
              return null;
            },
            emptyText: '',
            mobileOptions: {
              isInHeader: true,
              rank: 3,
            },
          },
        ]),
    {
      headerLabel: responsive.desktop ? undefined : 'Branches',
      headerComponent: () => {
        return (
          <TextTooltip
            text={'Manage the default account for branches from the Action menu.'}
            placement={'topLeft'}
          >
            <TableComponents.HeaderLabel responsive={responsive} numberOfLines={1}>
              Default for Branches
            </TableComponents.HeaderLabel>
          </TextTooltip>
        );
      },
      flex: 5,
      cellComponent: (accountGrant) => {
        return (
          <BranchCell
            accountGrant={accountGrant}
            isPrimaryGrantDisconnected={primaryAccountGrant?.status !== 'ACTIVE'}
          />
        );
      },
    },
    ...(editAccess === 'EDIT' && responsive.desktop
      ? [
          {
            width: 40,
            cellComponent: (accountGrant: AccountGrantModel) => {
              return (
                <AccountGrantActionsPopover
                  accountGrant={accountGrant}
                  handleUpdateAccountGrant={handleUpdateAccountGrant}
                  currentPrimaryAccount={primaryAccountGrant?.identifier}
                />
              );
            },
            mobileOptions: {
              isInHeader: true,
              rank: 1,
            },
          },
        ]
      : []),
  ];
};

const Content = ({
  accountGrants,
  handleUpdateAccountGrant,
  refetch,
  editAccess,
  viewer,
  handleConnectAccount,
}: {
  accountGrants: AccountGrantModel[];
  handleUpdateAccountGrant: (accountGrant: AccountGrantModel) => void;
  refetch: () => void;
  editAccess: EditAccessType;
  viewer: UserModel;
  handleConnectAccount: () => void;
}) => {
  const responsive = useResponsive();
  const sortedGrants = useMemo(() => {
    return accountGrants.sort(sortAccountGrants);
  }, [accountGrants]);

  const primaryAccountGrant = useMemo(() => {
    return sortedGrants.find((grant) => grant.isPrimary);
  }, [sortedGrants]);

  return (
    <React.Fragment>
      {editAccess !== 'EDIT' ? (
        <React.Fragment>
          <Callout
            text={
              editAccess === 'VIEW_NON_ADMIN'
                ? 'Contact the company admin to update, reconnect, or disconnect the default email account.'
                : 'You are viewing a branch. To make changes, switch to the main branch.'
            }
          />
          <Space height={24} />
        </React.Fragment>
      ) : null}
      <ExpiredPrimaryAccountBanner
        viewer={viewer}
        spacingBelowIfVisible={16}
        handleConnectAccount={handleConnectAccount}
      />
      <Table
        items={sortedGrants}
        isScrollable={!responsive.desktop}
        itemKey={'id'}
        columnDefinitions={getColumnDefinitions({
          handleUpdateAccountGrant,
          responsive,
          primaryAccountGrant,
          editAccess,
        })}
        hasBorder
        emptyStateText={
          'Connect at least one company account to send emails from the company or individual staff accounts.'
        }
      />
    </React.Fragment>
  );
};

const CommunicationAccountsTable = ({
  handleUpdateAccountGrant,
  editAccess,
  handleConnectAccount,
}: {
  handleUpdateAccountGrant: (accountGrant: AccountGrantModel) => void;
  editAccess: EditAccessType;
  handleConnectAccount: () => void;
}) => {
  const {loading, data, refetch} = useQuery<{viewer: UserModel}>(CommunicationAccountsTable.query, {
    fetchPolicy: 'cache-and-network',
  });

  return (
    <DataLoader loading={loading} as={PageLoadingIndicator} data={data}>
      {({loadedData}) => (
        <Content
          accountGrants={loadedData.viewer.viewingOrganization.allAccountGrants}
          handleUpdateAccountGrant={handleUpdateAccountGrant}
          refetch={refetch}
          editAccess={editAccess}
          viewer={loadedData.viewer}
          handleConnectAccount={handleConnectAccount}
        />
      )}
    </DataLoader>
  );
};

CommunicationAccountsTable.query = gql`
  ${AccountGrantActionsPopover.fragment}
  ${ExpiredPrimaryAccountBanner.fragment}

  query OrganizationSettingsCommunicationAccountsTable {

    ${gql.query}
      viewer {
        id
        viewingOrganization {
          id
          allAccountGrants {
            id
            status
            statusUpdatedAt
            isPrimary
            nylasGrantProvider
            identifier
            accountGrantOrganizationAssignments {
              id
              organization {
                id
                isDeleted
                name
                kind
              }
            }
            ...AccountGrantActionsPopover
          }
        }
        ...ExpiredPrimaryAccountBanner
      }
    }
`;

export default CommunicationAccountsTable;
